import React, { useEffect, useState } from 'react';
import { Book, Clock, Plus, Search, Edit, Trash2, Users, Loader2, Package, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import LoeschenMitGrundDialog, { grundHeader } from '../../../components/ui/loeschen-mit-grund-dialog';
import { Input } from '../../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';
import { toast } from 'sonner';
import { api } from '../../../lib/api';
import { getSubjectLabels } from '../../../lib/settings';
import { personTypesApi, type PersonType } from '../../../lib/person-types';
import RichTextEditor from '../../../components/ui/rich-text-editor';
import { useForm } from 'react-hook-form';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../components/ui/form';
import { PackageManagementForm } from '../../../components/packages/PackageManagementForm';
import WizardShell from '../../../components/ui/wizard-shell';
// Types
interface SubjectGalleryImage {
  id?: number;
  image_url: string;
  image_alt?: string | null;
  sort_order?: number;
}
interface SubjectWebsiteContent {
  seo_title?: string | null;
  seo_meta_description?: string | null;
  seo_canonical_url?: string | null;
  seo_og_title?: string | null;
  seo_og_description?: string | null;
  seo_og_image_url?: string | null;
  seo_twitter_title?: string | null;
  seo_twitter_description?: string | null;
  seo_twitter_image_url?: string | null;
}
interface Subject {
  id: string;
  name: string;
  category: string;
  description?: string;
  headerImageUrl?: string;
  featuredImageUrl?: string;
  websiteContent?: SubjectWebsiteContent;
  galleryImages?: SubjectGalleryImage[];
  standortIds: number[];
  active: boolean;
}
interface StandortOption {
  id: number;
  name: string;
}
interface TeachingFormat {
  id: string;
  name: string;
  type?: 'individual' | 'group' | 'ensemble' | 'workshop';
  artId?: number;
  artName?: string;
  duration: number; // in minutes
  maxStudents?: number;
  description?: string;
  active: boolean;
  signupFeeCents?: number;
}
// Subjects werden über die API geladen

const toApiList = <T,>(payload: unknown): T[] => {
  if (Array.isArray(payload)) return payload as T[];
  if (
    payload &&
    typeof payload === 'object' &&
    Array.isArray((payload as { items?: unknown }).items)
  ) {
    return (payload as { items: T[] }).items;
  }
  return [];
};

const MasterDataManagement = () => {
  // Lazy reload for person types when tab opens
  // State for subjects
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [subjectSearchTerm, setSubjectSearchTerm] = useState('');
  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(null);
  const [subjectDialogOpen, setSubjectDialogOpen] = useState(false);
  const [deleteSubjectDialogOpen, setDeleteSubjectDialogOpen] = useState(false);
  const [subjectEditorTab, setSubjectEditorTab] = useState('basis');
  const [isSubjectUploading, setIsSubjectUploading] = useState(false);
  const [standorte, setStandorte] = useState<StandortOption[]>([]);
  // State for teaching formats
  const [teachingFormats, setTeachingFormats] = useState<TeachingFormat[]>([]);
  const [formatTypes, setFormatTypes] = useState<Array<{ id: number; code: string; name: string; active: boolean }>>([]);
  const [formatSearchTerm, setFormatSearchTerm] = useState('');
  const [selectedFormat, setSelectedFormat] = useState<TeachingFormat | null>(null);
  const [formatDialogOpen, setFormatDialogOpen] = useState(false);
  const [deleteFormatDialogOpen, setDeleteFormatDialogOpen] = useState(false);
  const [packageManagementOpen, setPackageManagementOpen] = useState(false);
  const [selectedFormatForPackages, setSelectedFormatForPackages] = useState<number | null>(null);
  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  // Active tab state
  const [activeTab, setActiveTab] = useState('subjects');
  // Personentypen State
  const [personTypes, setPersonTypes] = useState<PersonType[]>([]);
  const [typeSearchTerm, setTypeSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<PersonType | null>(null);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [deleteTypeDialogOpen, setDeleteTypeDialogOpen] = useState(false);
  // Format Types (Unterrichtsform-Arten)
  const [formatTypeSearchTerm, setFormatTypeSearchTerm] = useState('');
  const [selectedFormatType, setSelectedFormatType] = useState<{ id?: number; code: string; name: string; beschreibung?: string; aktiv: boolean; trial_lesson_possible?: boolean; trial_lesson_duration_minutes?: number | null } | null>(null);
  const [formatTypeDialogOpen, setFormatTypeDialogOpen] = useState(false);
  const [deleteFormatTypeDialogOpen, setDeleteFormatTypeDialogOpen] = useState(false);
  const [trialLessonPossible, setTrialLessonPossible] = useState(false);
  // Form handling for subjects
  const subjectForm = useForm({
    defaultValues: {
      name: '',
      category: '',
      description: '',
      headerImageUrl: '',
      featuredImageUrl: '',
      galleryImages: [] as SubjectGalleryImage[],
      websiteContent: {
        seo_title: '',
        seo_meta_description: '',
        seo_canonical_url: '',
        seo_og_title: '',
        seo_og_description: '',
        seo_og_image_url: '',
        seo_twitter_title: '',
        seo_twitter_description: '',
        seo_twitter_image_url: '',
      } as SubjectWebsiteContent,
      standortIds: [] as number[],
      active: true,
    }
  });
  // Prefill subject form when opening dialog or switching selection
  useEffect(() => {
    if (!subjectDialogOpen) return;
    if (selectedSubject) {
      subjectForm.reset({
        name: selectedSubject.name || '',
        category: selectedSubject.category || '',
        description: selectedSubject.description || '',
        headerImageUrl: selectedSubject.headerImageUrl || '',
        featuredImageUrl: selectedSubject.featuredImageUrl || '',
        galleryImages: selectedSubject.galleryImages || [],
        websiteContent: {
          seo_title: selectedSubject.websiteContent?.seo_title || '',
          seo_meta_description: selectedSubject.websiteContent?.seo_meta_description || '',
          seo_canonical_url: selectedSubject.websiteContent?.seo_canonical_url || '',
          seo_og_title: selectedSubject.websiteContent?.seo_og_title || '',
          seo_og_description: selectedSubject.websiteContent?.seo_og_description || '',
          seo_og_image_url: selectedSubject.websiteContent?.seo_og_image_url || '',
          seo_twitter_title: selectedSubject.websiteContent?.seo_twitter_title || '',
          seo_twitter_description: selectedSubject.websiteContent?.seo_twitter_description || '',
          seo_twitter_image_url: selectedSubject.websiteContent?.seo_twitter_image_url || '',
        },
        standortIds: selectedSubject.standortIds || [],
        active: !!selectedSubject.active,
      });
    } else {
      subjectForm.reset({
        name: '',
        category: '',
        description: '',
        headerImageUrl: '',
        featuredImageUrl: '',
        galleryImages: [],
        websiteContent: {
          seo_title: '',
          seo_meta_description: '',
          seo_canonical_url: '',
          seo_og_title: '',
          seo_og_description: '',
          seo_og_image_url: '',
          seo_twitter_title: '',
          seo_twitter_description: '',
          seo_twitter_image_url: '',
        },
        standortIds: [],
        active: true,
      });
    }
  }, [subjectDialogOpen, selectedSubject]);
  useEffect(() => {
    if (subjectDialogOpen) setSubjectEditorTab('basis');
  }, [subjectDialogOpen]);
  // Form handling for formats
  // Format-Form wird über natives Formular gesteuert
  // Helper: Subjects laden (Mapping Backend -> Frontend)
  const fetchSubjects = async () => {
    const faecherResponse = await api.get<any>('/api/faecher?active_only=false&limit=200');
    const faecher = toApiList<any>(faecherResponse);
    const mapped: Subject[] = faecher.map((f: any) => ({
      id: String(f.id),
      name: f.name,
      category: f.kategorie ?? f.category ?? '',
      description: f.beschreibung ?? f.description ?? undefined,
      headerImageUrl: f.header_image_url ?? f.headerImageUrl ?? '',
      featuredImageUrl: f.featured_image_url ?? f.featuredImageUrl ?? '',
      websiteContent: f.website_content ?? f.websiteContent ?? {},
      galleryImages: f.gallery_images ?? f.galleryImages ?? [],
      standortIds: Array.isArray(f.standort_ids) ? f.standort_ids.map((id: any) => Number(id)).filter((id: number) => Number.isFinite(id) && id > 0) : [],
      active: (typeof f.aktiv === 'boolean') ? f.aktiv : (f.is_active ?? f.active ?? true),
    }));
    setSubjects(mapped);
  };
  const fetchStandorte = async () => {
    const standorteResponse = await api.get<any>('/api/standorte');
    const rows = toApiList<any>(standorteResponse);
    const mapped: StandortOption[] = rows.map((s: any) => ({
      id: Number(s.id),
      name: String(s.name ?? ''),
    })).filter((s) => Number.isFinite(s.id) && s.id > 0);
    setStandorte(mapped);
  };

  // Unterrichtsformen laden (API) + Arten
  const fetchTeachingFormats = async () => {
    const [formsResponse, typesResponse] = await Promise.all([
      api.get<any>('/api/unterrichtsformen?active_only=false&limit=200'),
      api.get<any>('/api/unterrichtsform-arten'),
    ]);
    const forms = toApiList<any>(formsResponse);
    const types = toApiList<any>(typesResponse);
    const typeMap = new Map<number, { id: number; code: string; name: string }>();
    types.forEach((t: any) => typeMap.set(Number(t.id), { id: Number(t.id), code: String(t.code), name: String(t.name) }));
    setFormatTypes(types.map((t: any) => ({ 
      id: Number(t.id), 
      code: String(t.code), 
      name: String(t.name), 
      active: !!(t.aktiv ?? t.is_active ?? t.active ?? true),
      trial_lesson_possible: !!(t.trial_lesson_possible ?? t.trialLessonPossible ?? false),
      trial_lesson_duration_minutes: t.trial_lesson_duration_minutes ?? t.trialLessonDurationMinutes ?? null
    })));
    const mapped: TeachingFormat[] = forms.map((f: any) => {
      const artId = f.art_id ?? f.artId ?? null;
      const art = artId ? typeMap.get(Number(artId)) : undefined;
      return {
        id: String(f.id),
        name: f.name,
        type: (f.typ ?? f.type) as TeachingFormat['type'],
        artId: artId ? Number(artId) : undefined,
        artName: art?.name,
        duration: Number(f.dauer ?? f.duration ?? 0),
        maxStudents: f.max_schueler ?? f.maxStudents ?? undefined,
        description: f.beschreibung ?? f.description ?? undefined,
        active: (typeof f.aktiv === 'boolean') ? f.aktiv : (f.is_active ?? f.active ?? true),
        signupFeeCents: f.signup_fee_cents !== null && f.signup_fee_cents !== undefined ? Number(f.signup_fee_cents) : undefined,
      };
    });
    setTeachingFormats(mapped);
  };

  // Load data
  const fetchPersonTypes = async () => {
    try {
      const types = await personTypesApi.list();
      setPersonTypes(types);
    } catch (error) {
      console.error('Error loading person types:', error);
      toast.error('Fehler beim Laden der Personentypen');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        await fetchSubjects();
        await fetchTeachingFormats();
        await fetchStandorte();
        // Personentypen initial laden
        await fetchPersonTypes();
      } catch (error) {
        console.error('Error loading master data:', error);
        toast.error('Fehler beim Laden der Stammdaten');
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  // Nachladen bei Tab-Wechsel, falls Liste noch leer ist
  useEffect(() => {
    if (activeTab === 'personTypes' && personTypes.length === 0 && !isLoading) {
      fetchPersonTypes();
    }
  }, [activeTab]);
  // Filtered person types
  const filteredTypes = personTypes.filter(t => t.name.toLowerCase().includes(typeSearchTerm.toLowerCase()) || (t.beschreibung || '').toLowerCase().includes(typeSearchTerm.toLowerCase()) || t.code.toLowerCase().includes(typeSearchTerm.toLowerCase()));
  // Save person type
  const handleTypeSave = async (event: React.FormEvent) => {
    event.preventDefault();
    const formData = new FormData(event.target as HTMLFormElement);
    const payload: Partial<PersonType> = {
      code: (formData.get('code') as string)?.trim(),
      name: (formData.get('name') as string)?.trim(),
      beschreibung: (formData.get('beschreibung') as string) || undefined,
      aktiv: (formData.get('aktiv') as string) === 'true',
      sort_order: formData.get('sort_order') ? parseInt(formData.get('sort_order') as string, 10) : undefined,
    };
    try {
      setIsLoading(true);
      if (selectedType) {
        await personTypesApi.update(selectedType.id, payload);
        toast.success(`Personentyp "${payload.name}" wurde aktualisiert`);
      } else {
        await personTypesApi.create(payload);
        toast.success(`Personentyp "${payload.name}" wurde erstellt`);
      }
      const types = await personTypesApi.list();
      setPersonTypes(types);
      setTypeDialogOpen(false);
      setSelectedType(null);
    } catch (e) {
      console.error('Error saving person type:', e);
      toast.error('Fehler beim Speichern des Personentyps');
    } finally {
      setIsLoading(false);
    }
  };
  // Delete person type
  const handleDeleteType = async () => {
    if (!selectedType) return;
    try {
      setIsLoading(true);
      await personTypesApi.delete(selectedType.id);
      toast.success(`Personentyp "${selectedType.name}" wurde gelöscht`);
      const types = await personTypesApi.list();
      setPersonTypes(types);
    } catch (e) {
      console.error('Error deleting person type:', e);
      toast.error('Fehler beim Löschen des Personentyps');
    } finally {
      setIsLoading(false);
      setDeleteTypeDialogOpen(false);
      setSelectedType(null);
    }
  };
  // Filter subjects based on search term
  const filteredSubjects = subjects.filter(subject => subject.name.toLowerCase().includes(subjectSearchTerm.toLowerCase()) || subject.category.toLowerCase().includes(subjectSearchTerm.toLowerCase()) || subject.description?.toLowerCase().includes(subjectSearchTerm.toLowerCase()));
  // Filter teaching formats based on search term
  const filteredFormats = teachingFormats.filter(format => format.name.toLowerCase().includes(formatSearchTerm.toLowerCase()) || format.description?.toLowerCase().includes(formatSearchTerm.toLowerCase()));
  const filteredFormatTypes = formatTypes.filter(t => t.name.toLowerCase().includes(formatTypeSearchTerm.toLowerCase()) || t.code.toLowerCase().includes(formatTypeSearchTerm.toLowerCase()));
  // Handle subject creation/edit
  const handleSubjectSave = async (data: any) => {
    try {
      setIsLoading(true);
      const payload = {
        name: data.name,
        kategorie: data.category,
        beschreibung: data.description,
        header_image_url: data.headerImageUrl || null,
        featured_image_url: data.featuredImageUrl || null,
        website_content: data.websiteContent || {},
        gallery_images: (data.galleryImages || []).map((img: SubjectGalleryImage, idx: number) => ({
          ...img,
          sort_order: idx,
        })),
        standort_ids: Array.isArray(data.standortIds) ? data.standortIds : [],
        is_active: data.active,
      };
      if (selectedSubject) {
        await api.patch(`/api/faecher/${selectedSubject.id}`, payload);
        toast.success(`${getSubjectLabels().singular} "${data.name}" wurde aktualisiert`);
      } else {
        await api.post('/api/faecher', payload);
        toast.success(`${getSubjectLabels().singular} "${data.name}" wurde erstellt`);
      }
      await fetchSubjects();
    } catch (error) {
      console.error('Error saving subject:', error);
      toast.error('Fehler beim Speichern des Fachs');
    } finally {
      setIsLoading(false);
      setSubjectDialogOpen(false);
      setSelectedSubject(null);
    }
  };
  // Handle teaching format creation/edit
  const handleFormatSave = async (event: React.FormEvent) => {
    event.preventDefault();
    const formData = new FormData(event.target as HTMLFormElement);
    const formatData: Partial<TeachingFormat> = {
      name: formData.get('name') as string,
      artId: formData.get('artId') ? parseInt(formData.get('artId') as string, 10) : undefined,
      type: (formData.get('type') as TeachingFormat['type']) || undefined,
      duration: parseInt(formData.get('duration') as string, 10),
      maxStudents: formData.get('maxStudents') ? parseInt(formData.get('maxStudents') as string, 10) : undefined,
      description: formData.get('description') as string,
      active: formData.get('active') === 'true'
    };
    try {
      setIsLoading(true);
      const payload: any = {
        name: formatData.name,
        dauer: formatData.duration,
        max_schueler: formatData.maxStudents,
        beschreibung: formatData.description,
        aktiv: formatData.active,
        is_active: formatData.active,
      };
      // Aufnahmegebühr
      const signupFeeStr = (formData.get('signup_fee_cents') as string) || '';
      if (signupFeeStr !== '') {
        const signupFeeNum = Number(signupFeeStr);
        if (Number.isFinite(signupFeeNum) && signupFeeNum >= 0) {
          payload.signup_fee_cents = Math.round(signupFeeNum * 100);
        }
      }
      if (formatData.artId) payload.art_id = formatData.artId;
      if (!formatData.artId && formatData.type) payload.typ = formatData.type;
      if (selectedFormat) {
        await api.patch(`/api/unterrichtsformen/${selectedFormat.id}`, payload);
        toast.success(`Unterrichtsform "${formatData.name}" wurde aktualisiert`);
      } else {
        await api.post('/api/unterrichtsformen', payload);
        toast.success(`Unterrichtsform "${formatData.name}" wurde erstellt`);
      }
      await fetchTeachingFormats();
    } catch (error) {
      console.error('Error saving teaching format:', error);
      toast.error('Fehler beim Speichern der Unterrichtsform');
    } finally {
      setIsLoading(false);
      setFormatDialogOpen(false);
      setSelectedFormat(null);
    }
  };
  // Handle subject deletion
  const handleDeleteSubject = async (grund: string) => {
    if (!selectedSubject) return;
    await api.delete(`/api/faecher/${selectedSubject.id}`, grundHeader(grund));
    toast.success(`${getSubjectLabels().singular} "${selectedSubject.name}" wurde gelöscht`);
    setDeleteSubjectDialogOpen(false);
    setSelectedSubject(null);
    await fetchSubjects();
  };
  // Handle teaching format deletion
  const handleDeleteFormat = async (grund: string) => {
    if (!selectedFormat) return;
    await api.delete(`/api/unterrichtsformen/${selectedFormat.id}`, grundHeader(grund));
    toast.success(`Unterrichtsform "${selectedFormat.name}" wurde gelöscht`);
    setDeleteFormatDialogOpen(false);
    setSelectedFormat(null);
    await fetchTeachingFormats();
  };
  // Save format type (Art)
  const handleFormatTypeSave = async (event: React.FormEvent) => {
    event.preventDefault();
    const form = new FormData(event.target as HTMLFormElement);
    const trialLessonPossibleValue = form.get('trial_lesson_possible') as string;
    // Sicherstellen, dass immer ein gültiger Wert gesetzt wird (Standard: false wenn leer oder null)
    const trialLessonPossible = trialLessonPossibleValue !== null && trialLessonPossibleValue !== '' && (trialLessonPossibleValue === 'true' || trialLessonPossibleValue === '1');
    const trialLessonDuration = form.get('trial_lesson_duration_minutes') ? Number(form.get('trial_lesson_duration_minutes') as string) : null;
    const payload = {
      code: String(form.get('code') || '').trim(),
      name: String(form.get('name') || '').trim(),
      beschreibung: (form.get('beschreibung') as string) || undefined,
      aktiv: (form.get('aktiv') as string) === 'true',
      trial_lesson_possible: trialLessonPossible,
      trial_lesson_duration_minutes: trialLessonPossible ? trialLessonDuration : null,
    };
    if (!payload.code || !payload.name) {
      toast.error('Bitte Code und Name angeben');
      return;
    }
    try {
      setIsLoading(true);
      if (selectedFormatType?.id) {
        await api.put(`/api/unterrichtsform-arten/${selectedFormatType.id}`, payload);
        toast.success(`Art "${payload.name}" aktualisiert`);
      } else {
        await api.post('/api/unterrichtsform-arten', payload);
        toast.success(`Art "${payload.name}" erstellt`);
      }
      await fetchTeachingFormats(); // lädt auch Arten neu
      setFormatTypeDialogOpen(false);
      setSelectedFormatType(null);
      setTrialLessonPossible(false);
    } catch (e) {
      console.error('Error saving format type:', e);
      toast.error('Fehler beim Speichern der Art');
    } finally {
      setIsLoading(false);
    }
  };
  // Delete format type (Art)
  const handleDeleteFormatType = async (grund: string) => {
    if (!selectedFormatType?.id) return;
    await api.delete(`/api/unterrichtsform-arten/${selectedFormatType.id}`, grundHeader(grund));
    toast.success(`Art "${selectedFormatType.name}" gelöscht`);
    setDeleteFormatTypeDialogOpen(false);
    setSelectedFormatType(null);
    await fetchTeachingFormats();
  };
  // Get type label for teaching format
  const getFormatTypeLabel = (type: TeachingFormat['type']) => {
    switch (type) {
      case 'individual':
        return 'Einzelunterricht';
      case 'group':
        return 'Gruppenunterricht';
      case 'ensemble':
        return 'Ensemble';
      case 'workshop':
        return 'Workshop';
      default:
        return type;
    }
  };
  const getFormatArtLabel = (f: TeachingFormat) => f.artName || getFormatTypeLabel(f.type as any);
  // Format duration display
  const formatDuration = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes} Min.`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return remainingMinutes > 0 ? `${hours} Std. ${remainingMinutes} Min.` : `${hours} Std.`;
    }
  };
  const ensureSelectedSubjectId = (): number | null => {
    if (!selectedSubject?.id) {
      toast.error(`Bitte ${getSubjectLabels().singular} zuerst speichern, dann Bilder hochladen`);
      return null;
    }
    const parsed = Number(selectedSubject.id);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      toast.error(`Ungültige ${getSubjectLabels().singular}-ID`);
      return null;
    }
    return parsed;
  };
  const uploadSubjectImage = async (file: File, fachId: number, category: string): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fach_id', String(fachId));
    formData.append('category', category);
    const response = await api.upload<{ url: string }>('/api/uploads/subject-image', formData);
    return String(response.url || '');
  };
  const handleSubjectImageUpload = async (
    file: File | undefined,
    category: string,
    applyUrl: (url: string) => void
  ) => {
    if (!file) return;
    const fachId = ensureSelectedSubjectId();
    if (!fachId) return;
    setIsSubjectUploading(true);
    try {
      const url = await uploadSubjectImage(file, fachId, category);
      if (!url) throw new Error('Keine URL zurückgegeben');
      applyUrl(url);
      toast.success('Bild hochgeladen');
    } catch (error) {
      console.error(error);
      toast.error('Bild-Upload fehlgeschlagen');
    } finally {
      setIsSubjectUploading(false);
    }
  };
  const addSubjectGalleryImage = () => {
    const current = subjectForm.getValues('galleryImages') || [];
    subjectForm.setValue(
      'galleryImages',
      [...current, { image_url: '', image_alt: '', sort_order: current.length }],
      { shouldDirty: true }
    );
  };
  const updateSubjectGalleryImage = (index: number, patch: Partial<SubjectGalleryImage>) => {
    const current = subjectForm.getValues('galleryImages') || [];
    const next = current.map((img, i) => (i === index ? { ...img, ...patch } : img));
    subjectForm.setValue('galleryImages', next, { shouldDirty: true });
  };
  const removeSubjectGalleryImage = (index: number) => {
    const current = subjectForm.getValues('galleryImages') || [];
    const next = current.filter((_, i) => i !== index);
    subjectForm.setValue('galleryImages', next, { shouldDirty: true });
  };
  const moveSubjectGalleryImage = (index: number, direction: -1 | 1) => {
    const current = [...(subjectForm.getValues('galleryImages') || [])];
    const target = index + direction;
    if (target < 0 || target >= current.length) return;
    [current[index], current[target]] = [current[target], current[index]];
    subjectForm.setValue('galleryImages', current, { shouldDirty: true });
  };
  // Render subject dialog
  const renderSubjectDialog = () => {
    const galleryImages = subjectForm.watch('galleryImages') || [];
    const seoValues = subjectForm.watch('websiteContent');
    const seoCount = [
      seoValues?.seo_title,
      seoValues?.seo_meta_description,
      seoValues?.seo_canonical_url,
      seoValues?.seo_og_title,
      seoValues?.seo_og_description,
      seoValues?.seo_og_image_url,
      seoValues?.seo_twitter_title,
      seoValues?.seo_twitter_description,
      seoValues?.seo_twitter_image_url,
    ].filter((value) => Boolean((value || '').toString().trim())).length;

    return (
      <WizardShell
        open={subjectDialogOpen}
        onOpenChange={setSubjectDialogOpen}
        title={selectedSubject ? `${getSubjectLabels().singular} bearbeiten` : `Neues ${getSubjectLabels().singular} erstellen`}
        description={`Website-Inhalte, Bilder und SEO für ${getSubjectLabels().singular} pflegen.`}
        onCancel={() => setSubjectDialogOpen(false)}
        onPrimary={() => subjectForm.handleSubmit(handleSubjectSave)()}
        primaryLabel={selectedSubject ? 'Speichern' : 'Erstellen'}
        canPrimary={!isLoading && !isSubjectUploading}
        isLoading={isLoading}
        contentClassName="w-[1650px] h-[95vh] max-w-[95vw] max-h-[95vh]"
      >
        <Form {...subjectForm}>
          <div className="space-y-4 py-2">
            <Tabs value={subjectEditorTab} onValueChange={setSubjectEditorTab}>
              <TabsList variant="line" className="w-full justify-start overflow-x-auto">
                <TabsTrigger variant="line" value="basis">Basis</TabsTrigger>
                <TabsTrigger variant="line" value="bilder" className="gap-2">
                  Bilder
                  <Badge size="sm" variant="secondary">{galleryImages.filter((img) => Boolean((img.image_url || '').trim())).length}</Badge>
                </TabsTrigger>
                <TabsTrigger variant="line" value="seo" className="gap-2">
                  SEO
                  <Badge size="sm" variant="secondary">{seoCount}</Badge>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="basis" className="space-y-4">
                <FormField control={subjectForm.control} name="name" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={subjectForm.control} name="category" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Kategorie</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={subjectForm.control} name="description" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Beschreibung (HTML)</FormLabel>
                    <FormControl>
                      <RichTextEditor value={field.value || ''} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={subjectForm.control} name="active" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <FormControl>
                      <Select value={field.value ? 'true' : 'false'} onValueChange={(val) => field.onChange(val === 'true')}>
                        <SelectTrigger>
                          <SelectValue placeholder="Status auswählen" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">Aktiv</SelectItem>
                          <SelectItem value="false">Inaktiv</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={subjectForm.control} name="standortIds" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Standorte</FormLabel>
                    <FormControl>
                      <div className="flex flex-wrap gap-2">
                        {standorte.map((standort) => {
                          const selected = Array.isArray(field.value) && field.value.includes(standort.id);
                          return (
                            <button
                              key={standort.id}
                              type="button"
                              onClick={() => {
                                const current = Array.isArray(field.value) ? field.value : [];
                                const next = selected
                                  ? current.filter((id) => id !== standort.id)
                                  : [...current, standort.id];
                                field.onChange(next);
                              }}
                              className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                                selected
                                  ? 'border-primary bg-primary/10 text-primary'
                                  : 'border-border bg-background text-foreground'
                              }`}
                            >
                              {standort.name}
                            </button>
                          );
                        })}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </TabsContent>

              <TabsContent value="bilder" className="space-y-4">
                <FormField control={subjectForm.control} name="headerImageUrl" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Headerbild URL</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <input
                      type="file"
                      accept="image/*"
                      disabled={isSubjectUploading || !selectedSubject?.id}
                      onChange={(event) => handleSubjectImageUpload(
                        event.target.files?.[0],
                        'header',
                        (url) => subjectForm.setValue('headerImageUrl', url, { shouldDirty: true })
                      )}
                    />
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={subjectForm.control} name="featuredImageUrl" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Featured Image URL (Card Hintergrund)</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <input
                      type="file"
                      accept="image/*"
                      disabled={isSubjectUploading || !selectedSubject?.id}
                      onChange={(event) => handleSubjectImageUpload(
                        event.target.files?.[0],
                        'featured',
                        (url) => subjectForm.setValue('featuredImageUrl', url, { shouldDirty: true })
                      )}
                    />
                    <FormMessage />
                  </FormItem>
                )} />

                <div className="space-y-3 rounded-md border p-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Bildergalerie (optional)</h4>
                    <Button type="button" variant="outline" size="sm" onClick={addSubjectGalleryImage}>
                      <Plus className="mr-2 h-4 w-4" />
                      Bild hinzufügen
                    </Button>
                  </div>
                  {galleryImages.map((image, index) => (
                    <div key={`${index}-${image.id ?? 'new'}`} className="grid gap-2 rounded border p-2">
                      <div className="flex items-center gap-2">
                        <Input
                          value={image.image_url || ''}
                          onChange={(e) => updateSubjectGalleryImage(index, { image_url: e.target.value })}
                          placeholder="Bild URL"
                        />
                        <input
                          type="file"
                          accept="image/*"
                          disabled={isSubjectUploading || !selectedSubject?.id}
                          onChange={(event) => handleSubjectImageUpload(
                            event.target.files?.[0],
                            'gallery',
                            (url) => updateSubjectGalleryImage(index, { image_url: url })
                          )}
                        />
                        <Button type="button" variant="outline" size="icon" onClick={() => moveSubjectGalleryImage(index, -1)}>
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button type="button" variant="outline" size="icon" onClick={() => moveSubjectGalleryImage(index, 1)}>
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        <Button type="button" variant="destructive" size="icon" onClick={() => removeSubjectGalleryImage(index)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <Input
                        value={image.image_alt || ''}
                        onChange={(e) => updateSubjectGalleryImage(index, { image_alt: e.target.value })}
                        placeholder="Alt-Text (optional)"
                      />
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="seo" className="space-y-3">
                <FormField control={subjectForm.control} name="websiteContent.seo_title" render={({ field }) => (
                  <FormItem>
                    <FormLabel>SEO Title</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value || ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={subjectForm.control} name="websiteContent.seo_meta_description" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Meta Description</FormLabel>
                    <FormControl>
                      <textarea
                        {...field}
                        value={field.value || ''}
                        className="flex min-h-[90px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={subjectForm.control} name="websiteContent.seo_canonical_url" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Canonical URL</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value || ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={subjectForm.control} name="websiteContent.seo_og_title" render={({ field }) => (
                  <FormItem>
                    <FormLabel>OG Title</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value || ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={subjectForm.control} name="websiteContent.seo_og_description" render={({ field }) => (
                  <FormItem>
                    <FormLabel>OG Description</FormLabel>
                    <FormControl>
                      <textarea
                        {...field}
                        value={field.value || ''}
                        className="flex min-h-[90px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={subjectForm.control} name="websiteContent.seo_og_image_url" render={({ field }) => (
                  <FormItem>
                    <FormLabel>OG Image URL</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value || ''} />
                    </FormControl>
                    <input
                      type="file"
                      accept="image/*"
                      disabled={isSubjectUploading || !selectedSubject?.id}
                      onChange={(event) => handleSubjectImageUpload(
                        event.target.files?.[0],
                        'seo-og',
                        (url) => subjectForm.setValue('websiteContent.seo_og_image_url', url, { shouldDirty: true })
                      )}
                    />
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={subjectForm.control} name="websiteContent.seo_twitter_title" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Twitter Title</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value || ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={subjectForm.control} name="websiteContent.seo_twitter_description" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Twitter Description</FormLabel>
                    <FormControl>
                      <textarea
                        {...field}
                        value={field.value || ''}
                        className="flex min-h-[90px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={subjectForm.control} name="websiteContent.seo_twitter_image_url" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Twitter Image URL</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value || ''} />
                    </FormControl>
                    <input
                      type="file"
                      accept="image/*"
                      disabled={isSubjectUploading || !selectedSubject?.id}
                      onChange={(event) => handleSubjectImageUpload(
                        event.target.files?.[0],
                        'seo-twitter',
                        (url) => subjectForm.setValue('websiteContent.seo_twitter_image_url', url, { shouldDirty: true })
                      )}
                    />
                    <FormMessage />
                  </FormItem>
                )} />
              </TabsContent>
            </Tabs>
          </div>
        </Form>
      </WizardShell>
    );
  };
  // Render teaching format dialog
  const renderFormatDialog = () => {
    return <Dialog open={formatDialogOpen} onOpenChange={setFormatDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedFormat ? 'Unterrichtsform bearbeiten' : 'Neue Unterrichtsform erstellen'}
            </DialogTitle>
            <DialogDescription>
              {selectedFormat ? 'Bearbeite die Details der Unterrichtsform.' : 'Füge eine neue Unterrichtsform zum System hinzu.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleFormatSave}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <label htmlFor="name" className="text-right text-sm font-medium">
                  Name
                </label>
                <Input id="name" name="name" defaultValue={selectedFormat?.name || ''} className="col-span-3" required />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <label htmlFor="artId" className="text-right text-sm font-medium">
                  Art
                </label>
                <Select name="artId" defaultValue={selectedFormat?.artId ? String(selectedFormat.artId) : ''}>
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Art auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {formatTypes.map(t => (
                      <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <label htmlFor="duration" className="text-right text-sm font-medium">
                  Dauer (Min.)
                </label>
                <Select name="duration" defaultValue={selectedFormat?.duration.toString() || '45'}>
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Dauer auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 Minuten</SelectItem>
                    <SelectItem value="45">45 Minuten</SelectItem>
                    <SelectItem value="60">60 Minuten</SelectItem>
                    <SelectItem value="90">90 Minuten</SelectItem>
                    <SelectItem value="120">120 Minuten</SelectItem>
                    <SelectItem value="180">180 Minuten</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <label htmlFor="maxStudents" className="text-right text-sm font-medium">
                  Max. Teilnehmer
                </label>
                <Input id="maxStudents" name="maxStudents" type="number" min="1" max="50" defaultValue={selectedFormat?.maxStudents?.toString() || ''} className="col-span-3" placeholder="Nur für Gruppen/Ensembles" />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <label htmlFor="description" className="text-right text-sm font-medium">
                  Beschreibung
                </label>
                <textarea id="description" name="description" defaultValue={selectedFormat?.description || ''} className="col-span-3 flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <label htmlFor="signup_fee_cents" className="text-right text-sm font-medium">
                  Aufnahmegebühr (€)
                </label>
                <Input 
                  id="signup_fee_cents" 
                  name="signup_fee_cents" 
                  type="number" 
                  step="0.01" 
                  min="0" 
                  defaultValue={selectedFormat?.signupFeeCents !== undefined ? (selectedFormat.signupFeeCents / 100).toFixed(2) : '0.00'} 
                  className="col-span-3" 
                />
                <div className="col-span-4 text-xs text-muted-foreground ml-[33%]">
                  Die Aufnahmegebühr wird pro Unterrichtsform festgelegt. Im Paket kann dann bestimmt werden, wann sie fällig wird.
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <label htmlFor="active" className="text-right text-sm font-medium">
                  Status
                </label>
                <Select name="active" defaultValue={selectedFormat?.active ? 'true' : 'false'}>
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Status auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Aktiv</SelectItem>
                    <SelectItem value="false">Inaktiv</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setFormatDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit">
                {selectedFormat ? 'Speichern' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>;
  };
  // Render delete subject dialog
  const renderDeleteSubjectDialog = () => {
    if (!selectedSubject) return null;
    return <LoeschenMitGrundDialog
        offen={deleteSubjectDialogOpen}
        titel={`${getSubjectLabels().singular} „${selectedSubject.name}“ löschen?`}
        onAbbrechen={() => setDeleteSubjectDialogOpen(false)}
        onLoeschen={handleDeleteSubject}
      />;
  };
  // Render delete format dialog
  const renderDeleteFormatDialog = () => {
    if (!selectedFormat) return null;
    return <LoeschenMitGrundDialog
        offen={deleteFormatDialogOpen}
        titel={`Unterrichtsform „${selectedFormat.name}“ löschen?`}
        onAbbrechen={() => setDeleteFormatDialogOpen(false)}
        onLoeschen={handleDeleteFormat}
      />;
  };
  return <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Stammdatenverwaltung</h1>
        <p className="text-muted-foreground">
          Verwalte {getSubjectLabels().plural}, Unterrichtsformen und andere Stammdaten
        </p>
      </div>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="subjects" className="flex items-center gap-1">
            <Book className="h-4 w-4" />
            {getSubjectLabels().plural}
          </TabsTrigger>
          <TabsTrigger value="formats" className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            Unterrichtsformen
          </TabsTrigger>
          <TabsTrigger value="formatTypes" className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            Unterrichtsform-Arten
          </TabsTrigger>
          <TabsTrigger value="personTypes" className="flex items-center gap-1">
            <Users className="h-4 w-4" />
            Personentypen
          </TabsTrigger>
        </TabsList>
        {/* Subjects Tab */}
        <TabsContent value="subjects" className="space-y-4">
          <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input type="search" placeholder={`${getSubjectLabels().plural} durchsuchen...`} className="pl-8 md:w-[300px]" value={subjectSearchTerm} onChange={e => setSubjectSearchTerm(e.target.value)} />
            </div>
            <Button onClick={() => {
            setSelectedSubject(null);
            subjectForm.reset({
              name: '',
              category: '',
              description: '',
              headerImageUrl: '',
              featuredImageUrl: '',
              galleryImages: [],
              websiteContent: {
                seo_title: '',
                seo_meta_description: '',
                seo_canonical_url: '',
                seo_og_title: '',
                seo_og_description: '',
                seo_og_image_url: '',
                seo_twitter_title: '',
                seo_twitter_description: '',
                seo_twitter_image_url: '',
              },
              standortIds: [],
              active: true,
            });
            setSubjectDialogOpen(true);
          }}>
              <Plus className="mr-2 h-4 w-4" />
              Neues {getSubjectLabels().singular}
            </Button>
          </div>
          {isLoading ? <div className="flex h-64 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div> : filteredSubjects.length === 0 ? <Card className="text-center p-8">
              <Book className="mx-auto h-12 w-12 text-muted-foreground" />
              <h2 className="mt-4 text-lg font-semibold">Keine {getSubjectLabels().plural} gefunden</h2>
              <p className="mt-2 text-sm text-muted-foreground">Füge neue {getSubjectLabels().plural} hinzu oder passe deine Suchkriterien an.</p>
              <Button className="mt-4" onClick={() => {
            setSelectedSubject(null);
            setSubjectDialogOpen(true);
          }}>
                <Plus className="mr-2 h-4 w-4" />
                Neues {getSubjectLabels().singular} erstellen
              </Button>
            </Card> : <Card>
              <CardHeader className="pb-1">
                <CardTitle>{getSubjectLabels().plural}</CardTitle>
                <CardDescription>{filteredSubjects.length} {getSubjectLabels().plural} gefunden</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{getSubjectLabels().singular}</TableHead>
                      <TableHead>Kategorie</TableHead>
                      <TableHead>Beschreibung</TableHead>
                      <TableHead>Standorte</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSubjects.map(subject => <TableRow key={subject.id}>
                        <TableCell className="font-medium">
                          {subject.name}
                        </TableCell>
                        <TableCell>{subject.category}</TableCell>
                        <TableCell className="max-w-[300px] truncate">
                          {subject.description || '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {subject.standortIds.length > 0 ? subject.standortIds.map((sid) => {
                              const standort = standorte.find((s) => s.id === sid);
                              return (
                                <Badge key={`${subject.id}-${sid}`} variant="secondary">
                                  {standort?.name || `#${sid}`}
                                </Badge>
                              );
                            }) : <span className="text-muted-foreground">-</span>}
                          </div>
                        </TableCell>
                        <TableCell>
                          {subject.active ? <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              Aktiv
                            </Badge> : <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
                              Inaktiv
                            </Badge>}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => {
                        setSelectedSubject(subject);
                        setSubjectDialogOpen(true);
                      }}>
                              <Edit className="h-4 w-4" />
                              <span className="sr-only">Bearbeiten</span>
                            </Button>
                            <Button variant="ghost" size="sm" className="text-destructive" onClick={() => {
                        setSelectedSubject(subject);
                        setDeleteSubjectDialogOpen(true);
                      }}>
                              <Trash2 className="h-4 w-4" />
                              <span className="sr-only">Löschen</span>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>}
        </TabsContent>
        {/* Teaching Formats Tab */}
        <TabsContent value="formats" className="space-y-4">
          <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input type="search" placeholder="Unterrichtsformen durchsuchen..." className="pl-8 md:w-[300px]" value={formatSearchTerm} onChange={e => setFormatSearchTerm(e.target.value)} />
            </div>
            <Button onClick={() => {
            setSelectedFormat(null);
            setFormatDialogOpen(true);
          }}>
              <Plus className="mr-2 h-4 w-4" />
              Neue Unterrichtsform
            </Button>
          </div>
          {isLoading ? <div className="flex h-64 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div> : filteredFormats.length === 0 ? <Card className="text-center p-8">
              <Clock className="mx-auto h-12 w-12 text-muted-foreground" />
              <h2 className="mt-4 text-lg font-semibold">
                Keine Unterrichtsformen gefunden
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Füge neue Unterrichtsformen hinzu oder passe deine
                Suchkriterien an.
              </p>
              <Button className="mt-4" onClick={() => {
            setSelectedFormat(null);
            setFormatDialogOpen(true);
          }}>
                <Plus className="mr-2 h-4 w-4" />
                Neue Unterrichtsform erstellen
              </Button>
            </Card> : <Card>
              <CardHeader className="pb-1">
                <CardTitle>Unterrichtsformen</CardTitle>
                <CardDescription>
                  {filteredFormats.length} Unterrichtsformen gefunden
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Art</TableHead>
                      <TableHead>Dauer</TableHead>
                      <TableHead>Max. Teilnehmer</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredFormats.map(format => <TableRow key={format.id}>
                        <TableCell className="font-medium">
                          {format.name}
                        </TableCell>
                        <TableCell>{getFormatArtLabel(format)}</TableCell>
                        <TableCell>{formatDuration(format.duration)}</TableCell>
                        <TableCell>{format.maxStudents || '-'}</TableCell>
                        <TableCell>
                          {format.active ? <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              Aktiv
                            </Badge> : <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
                              Inaktiv
                            </Badge>}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => {
                        setSelectedFormatForPackages(Number(format.id));
                        setPackageManagementOpen(true);
                      }} title="Pakete verwalten">
                              <Package className="h-4 w-4" />
                              <span className="sr-only">Pakete</span>
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => {
                        setSelectedFormat(format);
                        setFormatDialogOpen(true);
                      }}>
                              <Edit className="h-4 w-4" />
                              <span className="sr-only">Bearbeiten</span>
                            </Button>
                            <Button variant="ghost" size="sm" className="text-destructive" onClick={() => {
                        setSelectedFormat(format);
                        setDeleteFormatDialogOpen(true);
                      }}>
                              <Trash2 className="h-4 w-4" />
                              <span className="sr-only">Löschen</span>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>}
        </TabsContent>

        {/* Teaching Format Types Tab */}
        <TabsContent value="formatTypes" className="space-y-4">
          <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input type="search" placeholder="Arten durchsuchen..." className="pl-8 md:w-[300px]" value={formatTypeSearchTerm} onChange={e => setFormatTypeSearchTerm(e.target.value)} />
            </div>
            <Button onClick={() => { setSelectedFormatType(null); setTrialLessonPossible(false); setFormatTypeDialogOpen(true); }}>
              <Plus className="mr-2 h-4 w-4" /> Neue Art
            </Button>
          </div>
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredFormatTypes.length === 0 ? (
            <Card className="text-center p-8">
              <Clock className="mx-auto h-12 w-12 text-muted-foreground" />
              <h2 className="mt-4 text-lg font-semibold">Keine Arten gefunden</h2>
              <p className="mt-2 text-sm text-muted-foreground">Füge neue Unterrichtsform-Arten hinzu oder passe deine Suchkriterien an.</p>
              <Button className="mt-4" onClick={() => { setSelectedFormatType(null); setTrialLessonPossible(false); setFormatTypeDialogOpen(true); }}>
                <Plus className="mr-2 h-4 w-4" /> Neue Art erstellen
              </Button>
            </Card>
          ) : (
            <Card>
              <CardHeader className="pb-1">
                <CardTitle>Unterrichtsform-Arten</CardTitle>
                <CardDescription>{filteredFormatTypes.length} Arten gefunden</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Probestunde</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredFormatTypes.map(t => (
                      <TableRow key={t.id}>
                        <TableCell className="font-mono">{t.code}</TableCell>
                        <TableCell className="font-medium">{t.name}</TableCell>
                        <TableCell>
                          {t.trial_lesson_possible ? (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              Ja{t.trial_lesson_duration_minutes ? ` (${t.trial_lesson_duration_minutes} Min)` : ''}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">Nein</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {t.active ? (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Aktiv</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">Inaktiv</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => { 
                              const formatType = { id: t.id, code: t.code, name: t.name, aktiv: t.active, trial_lesson_possible: t.trial_lesson_possible, trial_lesson_duration_minutes: t.trial_lesson_duration_minutes }; 
                              setSelectedFormatType(formatType); 
                              setTrialLessonPossible(!!formatType.trial_lesson_possible); 
                              setFormatTypeDialogOpen(true); 
                            }}>
                              <Edit className="h-4 w-4" />
                              <span className="sr-only">Bearbeiten</span>
                            </Button>
                            <Button variant="ghost" size="sm" className="text-destructive" onClick={() => { setSelectedFormatType({ id: t.id, code: t.code, name: t.name, aktiv: t.active, trial_lesson_possible: t.trial_lesson_possible, trial_lesson_duration_minutes: t.trial_lesson_duration_minutes }); setDeleteFormatTypeDialogOpen(true); }}>
                              <Trash2 className="h-4 w-4" />
                              <span className="sr-only">Löschen</span>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
          {/* Dialog: Art */}
          <Dialog open={formatTypeDialogOpen} onOpenChange={(open) => {
            setFormatTypeDialogOpen(open);
            if (open && selectedFormatType) {
              setTrialLessonPossible(!!selectedFormatType.trial_lesson_possible);
            } else if (!open) {
              setTrialLessonPossible(false);
            }
          }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{selectedFormatType?.id ? 'Art bearbeiten' : 'Neue Art erstellen'}</DialogTitle>
                <DialogDescription>{selectedFormatType?.id ? 'Bearbeite die Unterrichtsform-Art.' : 'Füge eine neue Unterrichtsform-Art hinzu.'}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleFormatTypeSave} className="space-y-4">
                <div className="grid grid-cols-4 items-center gap-4">
                  <label htmlFor="code" className="text-right text-sm font-medium">Code</label>
                  <Input id="code" name="code" defaultValue={selectedFormatType?.code || ''} className="col-span-3" placeholder="z.B. individual, group" required={!selectedFormatType?.id} />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <label htmlFor="name" className="text-right text-sm font-medium">Name</label>
                  <Input id="name" name="name" defaultValue={selectedFormatType?.name || ''} className="col-span-3" required />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <label htmlFor="beschreibung" className="text-right text-sm font-medium">Beschreibung</label>
                  <textarea id="beschreibung" name="beschreibung" defaultValue={selectedFormatType?.beschreibung || ''} className="col-span-3 flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <label htmlFor="trial_lesson_possible" className="text-right text-sm font-medium">Probestunde möglich</label>
                  <Select 
                    name="trial_lesson_possible" 
                    defaultValue={selectedFormatType?.trial_lesson_possible ? 'true' : 'false'}
                    onValueChange={(value) => setTrialLessonPossible(value === 'true')}
                    required
                  >
                    <SelectTrigger className="col-span-3">
                      <SelectValue placeholder="Probestunde auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="false">Nein</SelectItem>
                      <SelectItem value="true">Ja</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {trialLessonPossible && (
                  <div className="grid grid-cols-4 items-center gap-4" id="trial_duration_container">
                    <label htmlFor="trial_lesson_duration_minutes" className="text-right text-sm font-medium">Probestunde Dauer (Min)</label>
                    <Input 
                      id="trial_lesson_duration_minutes" 
                      name="trial_lesson_duration_minutes" 
                      type="number" 
                      min="1" 
                      step="1"
                      defaultValue={selectedFormatType?.trial_lesson_duration_minutes || ''} 
                      className="col-span-3" 
                      placeholder="z.B. 30"
                      required={trialLessonPossible}
                    />
                  </div>
                )}
                <div className="grid grid-cols-4 items-center gap-4">
                  <label htmlFor="aktiv" className="text-right text-sm font-medium">Status</label>
                  <Select name="aktiv" defaultValue={selectedFormatType?.aktiv ? 'true' : 'false'}>
                    <SelectTrigger className="col-span-3">
                      <SelectValue placeholder="Status auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Aktiv</SelectItem>
                      <SelectItem value="false">Inaktiv</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button variant="outline" type="button" onClick={() => setFormatTypeDialogOpen(false)}>Abbrechen</Button>
                  <Button type="submit">{selectedFormatType?.id ? 'Speichern' : 'Erstellen'}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          {/* Dialog: Art löschen */}
          <LoeschenMitGrundDialog
            offen={deleteFormatTypeDialogOpen}
            titel={`Unterrichtsform-Art „${selectedFormatType?.name ?? ''}“ löschen?`}
            onAbbrechen={() => setDeleteFormatTypeDialogOpen(false)}
            onLoeschen={handleDeleteFormatType}
          />
        </TabsContent>

        {/* Person Types Tab */}
        <TabsContent value="personTypes" className="space-y-4">
          <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input type="search" placeholder="Personentypen durchsuchen..." className="pl-8 md:w-[300px]" value={typeSearchTerm} onChange={e => setTypeSearchTerm(e.target.value)} />
            </div>
            <Button onClick={() => { setSelectedType(null); setTypeDialogOpen(true); }}>
              <Plus className="mr-2 h-4 w-4" />
              Neuer Personentyp
            </Button>
          </div>
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredTypes.length === 0 ? (
            <Card className="text-center p-8">
              <Users className="mx-auto h-12 w-12 text-muted-foreground" />
              <h2 className="mt-4 text-lg font-semibold">Keine Personentypen gefunden</h2>
              <p className="mt-2 text-sm text-muted-foreground">Füge neue Personentypen hinzu oder passe deine Suchkriterien an.</p>
              <Button className="mt-4" onClick={() => { setSelectedType(null); setTypeDialogOpen(true); }}>
                <Plus className="mr-2 h-4 w-4" />
                Neuen Personentyp erstellen
              </Button>
            </Card>
          ) : (
            <Card>
              <CardHeader className="pb-1">
                <CardTitle>Personentypen</CardTitle>
                <CardDescription>{filteredTypes.length} Personentypen gefunden</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Beschreibung</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Sortierung</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTypes.map(t => (
                      <TableRow key={t.id}>
                        <TableCell className="font-mono">{t.code}</TableCell>
                        <TableCell className="font-medium">{t.name}</TableCell>
                        <TableCell className="max-w-[300px] truncate">{t.beschreibung || '-'}</TableCell>
                        <TableCell>
                          {t.aktiv ? (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Aktiv</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">Inaktiv</Badge>
                          )}
                        </TableCell>
                        <TableCell>{t.sort_order ?? '-'}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => { setSelectedType(t); setTypeDialogOpen(true); }}>
                              <Edit className="h-4 w-4" />
                              <span className="sr-only">Bearbeiten</span>
                            </Button>
                            <Button variant="ghost" size="sm" className="text-destructive" onClick={() => { setSelectedType(t); setDeleteTypeDialogOpen(true); }}>
                              <Trash2 className="h-4 w-4" />
                              <span className="sr-only">Löschen</span>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
          {/* Dialog: Personentyp */}
          <Dialog open={typeDialogOpen} onOpenChange={setTypeDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{selectedType ? 'Personentyp bearbeiten' : 'Neuen Personentyp erstellen'}</DialogTitle>
                <DialogDescription>{selectedType ? 'Bearbeite den Personentyp.' : 'Füge einen neuen Personentyp hinzu.'}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleTypeSave} className="space-y-4">
                <div className="grid grid-cols-4 items-center gap-4">
                  <label htmlFor="code" className="text-right text-sm font-medium">Code</label>
                  <Input id="code" name="code" defaultValue={selectedType?.code || ''} className="col-span-3" placeholder="z.B. customer, student, teacher" required={!selectedType} />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <label htmlFor="name" className="text-right text-sm font-medium">Name</label>
                  <Input id="name" name="name" defaultValue={selectedType?.name || ''} className="col-span-3" required />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <label htmlFor="beschreibung" className="text-right text-sm font-medium">Beschreibung</label>
                  <textarea id="beschreibung" name="beschreibung" defaultValue={selectedType?.beschreibung || ''} className="col-span-3 flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"></textarea>
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <label htmlFor="aktiv" className="text-right text-sm font-medium">Status</label>
                  <Select name="aktiv" defaultValue={selectedType?.aktiv ? 'true' : 'false'}>
                    <SelectTrigger className="col-span-3">
                      <SelectValue placeholder="Status auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Aktiv</SelectItem>
                      <SelectItem value="false">Inaktiv</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <label htmlFor="sort_order" className="text-right text-sm font-medium">Sortierung</label>
                  <Input id="sort_order" name="sort_order" type="number" min="0" defaultValue={selectedType?.sort_order?.toString() || ''} className="col-span-3" />
                </div>
                <DialogFooter>
                  <Button variant="outline" type="button" onClick={() => setTypeDialogOpen(false)}>Abbrechen</Button>
                  <Button type="submit">{selectedType ? 'Speichern' : 'Erstellen'}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          {/* Dialog: Personentyp löschen */}
          <Dialog open={deleteTypeDialogOpen} onOpenChange={setDeleteTypeDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Personentyp löschen</DialogTitle>
                <DialogDescription>Bist du sicher, dass du den Personentyp "{selectedType?.name}" löschen möchtest? Diese Aktion kann nicht rückgängig gemacht werden.</DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDeleteTypeDialogOpen(false)}>Abbrechen</Button>
                <Button variant="destructive" onClick={handleDeleteType}>Löschen</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
      {/* Dialogs */}
      {renderSubjectDialog()}
      {renderDeleteSubjectDialog()}
      {renderFormatDialog()}
      {renderDeleteFormatDialog()}
      
      {/* Paketverwaltung Dialog */}
      <Dialog open={packageManagementOpen} onOpenChange={setPackageManagementOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          {selectedFormatForPackages && (
            <PackageManagementForm
              unterrichtsformId={selectedFormatForPackages}
              onClose={() => {
                setPackageManagementOpen(false);
                setSelectedFormatForPackages(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>;
};
export default MasterDataManagement;